import { useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import type { Profile } from '@/types/database';

interface LoginParams {
  email: string;
  password: string;
}

interface RegisterParams {
  email: string;
  password: string;
  fullName: string;
}

export function useAuth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    session,
    user,
    profile,
    isLoading,
    isInitialized,
    setSession,
    setProfile,
    setLoading,
    setInitialized,
    reset,
  } = useAuthStore();

  const fetchProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Failed to fetch profile:', error.message);
      return null;
    }

    setProfile(data as Profile);
    return data as Profile;
  }, [setProfile]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      if (currentSession?.user) {
        fetchProfile(currentSession.user.id);
      }
      setLoading(false);
      setInitialized(true);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [setSession, setProfile, setLoading, setInitialized, fetchProfile]);

  const login = useCallback(
    async ({ email, password }: LoginParams) => {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setLoading(false);
        throw error;
      }

      const redirect = searchParams.get('redirect');
      navigate(redirect || '/dashboard');
    },
    [navigate, setLoading, searchParams],
  );

  const register = useCallback(
    async ({ email, password, fullName }: RegisterParams) => {
      setLoading(true);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
        },
      });

      setLoading(false);

      if (error) {
        throw error;
      }
    },
    [setLoading],
  );

  const resetPassword = useCallback(
    async (email: string) => {
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setLoading(false);
      if (error) throw error;
    },
    [setLoading],
  );

  const updatePassword = useCallback(
    async (newPassword: string) => {
      setLoading(true);
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      setLoading(false);
      if (error) throw error;
    },
    [setLoading],
  );

  const logout = useCallback(async () => {
    setLoading(true);
    const { error } = await supabase.auth.signOut();
    if (error) {
      setLoading(false);
      throw error;
    }
    reset();
    navigate('/login');
  }, [navigate, setLoading, reset]);

  const updateProfile = useCallback(
    async (updates: Partial<Pick<Profile, 'full_name' | 'avatar_url' | 'preferred_ai_model'>>) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;
      setProfile(data as Profile);
      return data as Profile;
    },
    [user, setProfile],
  );

  return {
    session,
    user,
    profile,
    isLoading,
    isInitialized,
    isAuthenticated: !!session,
    login,
    register,
    logout,
    resetPassword,
    updatePassword,
    updateProfile,
    fetchProfile,
  };
}
